import React from 'react';

import { Badge, Button, Icon } from '../../../components/common';
import { useAppStore } from '../../../store/useAppStore';
import {
    buildRehearsalSpeechTargets,
    deriveDefaultRehearsalCharacterIds,
    orderRehearsalScriptBlocks,
} from '../../show-structure/utils/rehearsal-script';
import { useDashboardAccessibleToolIds } from '../logic/useDashboardAccessibleToolIds';
import { useDashboardNavigation } from '../logic/useDashboardNavigation';
import { DashboardWidgetCard } from './DashboardWidgetCard';

function pluralize(count: number, singular: string): string {
    return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

export const LineStudyWidget: React.FC = () => {
    const personnelId = useAppStore(state => state.cloud.member?.personnelId ?? null);
    const assignments = useAppStore(state => state.castAssignments || []);
    const acts = useAppStore(state => state.showStructure.acts || []);
    const scenes = useAppStore(state => state.showStructure.scenes || []);
    const characters = useAppStore(state => state.showStructure.characters || []);
    const scriptBlocks = useAppStore(state => state.showStructure.scriptBlocks || []);
    const openTool = useDashboardNavigation();
    const accessibleToolIds = useDashboardAccessibleToolIds();

    if (!accessibleToolIds.has('rehearsal-script')) {
        return null;
    }

    const selectedCharacterIds = deriveDefaultRehearsalCharacterIds({
        personnelId,
        assignments,
        characters,
    });
    const selectedCharacters = characters.filter(character => selectedCharacterIds.includes(character.id));
    const orderedBlocks = orderRehearsalScriptBlocks(scriptBlocks, acts, scenes);
    const speechTargets = buildRehearsalSpeechTargets(orderedBlocks, selectedCharacterIds, characters);
    const pageCount = new Set(speechTargets.map(target => target.pageNumber)).size;

    return (
        <DashboardWidgetCard
            title="Line Study"
            icon="auto_stories"
            footer={(
                <Button
                    aria-label="Open Rehearsal Script"
                    onClick={() => openTool('rehearsal-script')}
                    size="sm"
                    variant="ghost"
                >
                    Script
                </Button>
            )}
            testId="line-study-widget"
        >
            {!personnelId ? (
                <div className="text-center py-6">
                    <Icon name="person_search" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No linked cast profile</p>
                </div>
            ) : selectedCharacters.length === 0 ? (
                <div className="text-center py-6">
                    <Icon name="theater_comedy" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No assigned characters</p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {selectedCharacters.map(character => (
                            <Badge key={character.id} variant="indigo" size="sm">{character.name}</Badge>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{speechTargets.length}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {pluralize(speechTargets.length, 'speech')}
                            </p>
                        </div>
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pageCount}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {pluralize(pageCount, 'page')}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </DashboardWidgetCard>
    );
};
